// "Knowledge base" block on the run page (design §10, contract §5): the
// pages a run read (`kb_read`) and wrote (`kb_write`), with links to them.
// Data comes from `GET /workspaces/:id/kb/runs/:runId/activity`; while the
// run is live it is refreshed whenever a kb event arrives over SSE.

import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import type { RunEvent } from '@goblic/shared'
import { useKbRunActivity, useKbSpaces } from '../../hooks/useKnowledge'
import { formatDateTime, formatTime } from '../../lib/format'
import { Panel } from '../ui'

/** A `kb_read` / `kb_write` run event (run_event_type values from migration 0018). */
export interface KbRunEventView {
  type: 'kb_read' | 'kb_write'
  space: string
  path: string
  rev: number | null
  status: 'applied' | 'proposed' | 'conflict' | null
  message: string | null
}

/**
 * Reads a kb event off the timeline. The shared `RunEvent` union may not
 * list these types (yet), so this checks the shape instead of relying on it;
 * fields may sit on the event itself or in a `payload` object.
 */
export function kbRunEventOf(event: RunEvent | { type: string }): KbRunEventView | null {
  const raw = event as unknown as Record<string, unknown>
  if (raw.type !== 'kb_read' && raw.type !== 'kb_write') return null
  const fields =
    raw.payload && typeof raw.payload === 'object'
      ? { ...raw, ...(raw.payload as Record<string, unknown>) }
      : raw
  const status = fields.status
  return {
    type: raw.type,
    space: typeof fields.space === 'string' ? fields.space : 'workspace',
    path: typeof fields.path === 'string' ? fields.path : '',
    rev: typeof fields.rev === 'number' ? fields.rev : null,
    status:
      status === 'applied' || status === 'proposed' || status === 'conflict' ? status : null,
    message: typeof fields.message === 'string' ? fields.message : null,
  }
}

const WRITE_LABEL: Record<'applied' | 'proposed' | 'conflict', { text: string; className: string }> = {
  applied: { text: 'saved', className: 'bg-ok-soft text-ok' },
  proposed: { text: 'proposed', className: 'bg-info-soft text-info' },
  conflict: { text: 'conflict', className: 'bg-signal-soft text-signal' },
}

export function KbPageLink({ space, path }: { space: string; path: string }) {
  return (
    <Link
      to="/knowledge/$space/$"
      params={{ space, _splat: path }}
      className="link break-all font-mono text-sm"
    >
      {path}
    </Link>
  )
}

export function RunKnowledgePanel({
  runId,
  live,
  events,
}: {
  runId: string
  live: boolean
  events: RunEvent[]
}) {
  const activityQuery = useKbRunActivity(runId, { live })
  const { labelOf } = useKbSpaces()
  const kbEventCount = events.filter((event) => kbRunEventOf(event) != null).length
  const { refetch } = activityQuery

  useEffect(() => {
    if (kbEventCount > 0) void refetch()
  }, [kbEventCount, refetch])

  const reads = activityQuery.data?.reads ?? []
  const writes = activityQuery.data?.writes ?? []

  return (
    <Panel title="Knowledge base" description="Pages this run read and wrote.">
      {activityQuery.isLoading ? (
        <p className="text-sm text-ink-3">Loading…</p>
      ) : activityQuery.isError ? (
        <p className="text-sm text-danger">Couldn’t load the knowledge activity.</p>
      ) : reads.length === 0 && writes.length === 0 ? (
        <p className="text-sm text-ink-3">
          {live ? 'No knowledge pages read or written yet.' : 'No knowledge pages read or written.'}
        </p>
      ) : (
        <div className="space-y-4">
          {writes.length ? (
            <div>
              <h3 className="mb-1.5 text-sm font-medium text-ink-2">Wrote</h3>
              <ul className="space-y-1.5">
                {writes.map((write, index) => {
                  const label = WRITE_LABEL[write.status]
                  return (
                    <li key={`${write.revisionId ?? write.proposalId ?? index}`} className="text-sm">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <KbPageLink space={write.space} path={write.path} />
                        <span className={`rounded-full px-2 text-xs font-medium ${label.className}`}>
                          {label.text}
                          {write.rev != null ? ` · rev ${write.rev}` : ''}
                        </span>
                      </span>
                      <span className="block text-xs text-ink-3" title={formatDateTime(write.at)}>
                        {labelOf(write.space)} · {formatTime(write.at)}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : null}
          {reads.length ? (
            <div>
              <h3 className="mb-1.5 text-sm font-medium text-ink-2">Read</h3>
              <ul className="space-y-1.5">
                {reads.map((read) => (
                  <li key={`${read.pageId}|${read.rev}`} className="text-sm">
                    <KbPageLink space={read.space} path={read.path} />
                    <span className="block text-xs text-ink-3" title={formatDateTime(read.at)}>
                      {labelOf(read.space)} · rev {read.rev}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  )
}
